import { randomUUID } from 'node:crypto';
import { BadRequestException, Injectable, NotFoundException, Optional } from '@nestjs/common';
import { PrismaService } from '@cart/api-data-access';
import { InvoiceService } from '../invoices/invoice.service';
import { InvoicePdfService } from '../invoices/invoice-pdf.service';
import { InvoiceLinkService } from '../invoices/invoice-link.service';
import { OrderListResponse, OrderStatus, OrderSummary } from '@cart/shared-types';
import { AuditService } from '../audit/audit.service';
import { CartService } from '../cart/cart.service';
import { EmailService } from '../notifications/email.service';
import { OrdersRepository } from './orders.repository';

// Maps every status this service can transition an order to onto its email
// template type code — 'placed' is deliberately absent: nothing ever calls
// updateStatus(id, 'placed') in practice (that's the value an order is
// *created* with, not transitioned to), so "Order Placed" is triggered from
// createFromCart() instead, right where the order actually comes into being.
const STATUS_TEMPLATE_CODES: Partial<Record<OrderStatus, string>> = {
  processing: 'order_processing',
  shipped: 'order_shipped',
  out_for_delivery: 'order_out_for_delivery',
  delivered: 'order_delivered',
  cancelled: 'order_cancelled',
};

function formatCurrency(amountInPaise: number): string {
  return `₹${(amountInPaise / 100).toFixed(2)}`;
}

@Injectable()
export class OrdersService {
  constructor(
    private readonly cartService: CartService,
    private readonly ordersRepository: OrdersRepository,
    @Optional()
    private readonly auditService?: AuditService,
    @Optional()
    private readonly emailService?: EmailService,
    @Optional()
    private readonly prisma?: PrismaService,
    @Optional()
    private readonly invoices?: InvoiceService,
    @Optional()
    private readonly invoicePdfs?: InvoicePdfService,
    @Optional()
    private readonly invoiceLinks?: InvoiceLinkService
  ) {}

  async list(userId?: string): Promise<OrderListResponse> {
    const items = await this.ordersRepository.list(userId);

    return {
      items,
      total: items.length,
    };
  }

  async createFromCart(userId: string): Promise<OrderSummary> {
    const cart = await this.cartService.getCart(userId);

    if (!cart.items.length) {
      throw new BadRequestException('cart is empty');
    }

    const order: OrderSummary = {
      id: `order-${Date.now()}`,
      status: 'placed',
      // A random id per line — a deterministic `order-item-${productId}` id
      // collides the moment the same product appears in a second order.
      items: cart.items.map((item) => ({
        id: randomUUID(),
        productId: item.productId,
        productName: item.productName,
        productSlug: item.productSlug,
        unitPrice: item.unitPrice,
        quantity: item.quantity,
        lineTotal: item.lineTotal,
      })),
      subtotal: cart.subtotal,
      discountTotal: cart.discountTotal,
      grandTotal: cart.total,
      totalItems: cart.totalItems,
      createdAt: new Date().toISOString(),
    };

    const created = await this.ordersRepository.create(order, userId);
    await this.cartService.clear(userId);

    await this.auditService?.logChange({
      schemaName: 'sales',
      tableName: 'orders',
      recordId: created.id,
      operation: 'create',
      changedColumns: ['items', 'subtotal', 'status'],
      newValue: created,
      source: 'OrdersService.createFromCart',
    });

    if (created.customerEmail) {
      // The invoice is attached here, which makes the confirmation email the
      // usual FIRST caller of ensureInvoice — so the render happens on a
      // background send rather than in front of a waiting customer. That is
      // most of what a queue would buy, without a queue existing.
      const invoice = await this.attachInvoice(created.id);

      await this.emailService?.sendTemplated(
        'order_placed',
        created.customerEmail,
        {
          customerName: created.customerName ?? 'there',
          orderNumber: created.orderNumber ?? created.id,
          grandTotal: formatCurrency(created.grandTotal ?? created.subtotal),
          itemsList: created.items.map((item) => `${item.productName} × ${item.quantity}`).join(', '),
        },
        invoice ? [invoice] : undefined
      );
    }

    return created;
  }

  /**
   * The invoice PDF for an order, as an email attachment.
   *
   * Fail-soft on purpose, like every other side effect on this path: a
   * merchant's order confirmation must still go out if Chromium is broken or
   * R2 is unreachable. The invoice row is issued regardless, so the customer
   * can download it later — the attachment is a convenience, not the record.
   */
  /**
   * A signed download link for an order's invoice.
   *
   * Fail-soft, like every side effect on this path: a delivery notification
   * must still go out if the invoice cannot be issued. The template renders an
   * empty variable rather than a broken link.
   */
  private async invoiceLink(orderId: string): Promise<string | null> {
    if (!this.invoices || !this.invoiceLinks) return null;

    try {
      const invoice = await this.invoices.ensureInvoice(orderId, 'order_delivered');
      return this.invoiceLinks.urlFor(invoice.id);
    } catch {
      return null;
    }
  }

  private async attachInvoice(orderId: string) {
    if (!this.invoices || !this.invoicePdfs) return null;

    try {
      const invoice = await this.invoices.ensureInvoice(orderId, 'order_placed');
      const pdf = await this.invoicePdfs.pdfFor(invoice.id);

      return {
        filename: pdf.fileName,
        content: pdf.buffer,
        contentType: 'application/pdf',
      };
    } catch {
      return null;
    }
  }

  async detail(orderId: string, userId?: string): Promise<OrderSummary> {
    const order = await this.ordersRepository.findById(orderId, userId);

    if (!order) {
      throw new NotFoundException('Order was not found');
    }

    return order;
  }

  async updateStatus(orderId: string, status: OrderStatus): Promise<OrderSummary> {
    this.validateStatus(status);

    const updated = await this.ordersRepository.updateStatus(orderId, status);

    if (!updated) {
      throw new NotFoundException('Order was not found');
    }

    await this.auditService?.logChange({
      schemaName: 'sales',
      tableName: 'orders',
      recordId: updated.id,
      operation: 'update',
      changedColumns: ['status'],
      newValue: updated,
      source: 'OrdersService.updateStatus',
    });

    await this.sendStatusEmail(updated);

    return updated;
  }

  private async sendStatusEmail(order: OrderSummary): Promise<void> {
    const typeCode = STATUS_TEMPLATE_CODES[order.status];

    if (!typeCode || !order.customerEmail) {
      return;
    }

    const variables: Record<string, string> = {
      customerName: order.customerName ?? 'there',
      orderNumber: order.orderNumber ?? order.id,
      reason: '',
      carrier: '',
      trackingNumber: '',
    };

    if (order.status === 'shipped') {
      const shipment = await this.prisma?.shipment.findFirst({
        where: { orderId: order.id },
        orderBy: { createdAt: 'desc' },
      });
      variables['carrier'] = shipment?.carrier ?? '';
      variables['trackingNumber'] = shipment?.trackingNumber ?? '';
    }

    // The tax invoice goes out on delivery, as a link.
    //
    // Delivery rather than placement because for goods the invoice accompanies
    // the supply, and a link rather than an attachment because an invoice that
    // has to survive in a mailbox is better fetched fresh than pinned to one
    // message — the same URL keeps working while the link is valid, and after
    // that the customer signs in and downloads it.
    variables['invoiceUrl'] = '';
    if (order.status === 'delivered') {
      variables['invoiceUrl'] = (await this.invoiceLink(order.id)) ?? '';
    }

    await this.emailService?.sendTemplated(typeCode, order.customerEmail, variables);
  }

  private validateStatus(status: OrderStatus): void {
    const allowed: OrderStatus[] = [
      'placed',
      'processing',
      'shipped',
      'out_for_delivery',
      'delivered',
      'cancelled',
    ];

    if (!allowed.includes(status)) {
      throw new BadRequestException('Invalid order status');
    }
  }
}
